import orderBy from 'lodash/orderBy'
import { collection } from '../infra/collection'
import { LegendVersionId, PublishedLegend } from './PublishedLegend'
import { FindLegendsQuery, LegendDTO } from './dtos/legend'
import { PrimeWorkspace } from '../iam/WorkspaceStore'
import { CursorPaged, Id } from '../types/definitions'
import { LegendAPI } from './API'

/**
 * Prime Published Legend.
 */
export type PrimePublishedLegend = (props: LegendVersionId) => PublishedLegend

/**
 * Find published legends.
 */
export type FindLegends = (
  query: FindLegendsQuery
) => Promise<CursorPaged<PublishedLegend>>

/**
 * Store for Published Legends.
 */
export default class PublishedLegendStore {
  /**
   * Published legends.
   */
  publishedLegends = collection<LegendDTO, PublishedLegend>({
    create: () => new PublishedLegend(this.primeWorkspace),
    // Each version is a different instance.
    getDataId: (attrs) =>
      LegendVersionId.asString(
        LegendVersionId(attrs.id || '?', attrs.version ?? -1)
      ),
    getModelId: (model) => LegendVersionId.asString(model.versionId),
  })

  /**
   * Constructor.
   *
   * @param api
   * @param primeWorkspace
   */
  constructor(private api: LegendAPI, private primeWorkspace: PrimeWorkspace) {}

  /**
   * Primes a Published Legend.
   *
   * @param versionId
   */
  primePublishedLegend: PrimePublishedLegend = (versionId) =>
    this.publishedLegends.set(versionId)

  /**
   * Gets the published legend version by ID from the in-memory collection.
   *
   * @param legendVersionId
   */
  get(legendVersionId: LegendVersionId): PublishedLegend {
    return (
      this.publishedLegends.find((l) =>
        LegendVersionId.equals(l.versionId, legendVersionId)
      ) || PublishedLegend.none
    )
  }

  /**
   * Returns published legends for the specified Workspace, sorted by View Order.
   * Only includes latest versions.
   *
   * @param workspaceId
   */
  forWorkspace(workspaceId: Id): PublishedLegend[] {
    workspaceId = Id.assert(workspaceId)
    const seen = new Map<string, PublishedLegend>()
    this.publishedLegends.forEach((legend) => {
      if (!Id.some(legend.id)) {
        return
      }

      if (legend.workspace.id !== workspaceId) {
        return
      }

      const existing = seen.get(legend.id)
      if (existing && existing.version > legend.version) {
        return
      }

      seen.set(legend.id, legend)
    })

    return orderBy(
      // NOTE: Need to apply inactive filter after having
      // accumulated the latest versions.
      Array.from(seen.values()).filter((legend) => !legend.inactive),
      [(x) => x.viewOrder, (x) => x.dateCreated],
      ['asc', 'asc']
    )
  }

  /**
   * Find legends.
   *
   * @param query
   */
  async findLegends(
    query: FindLegendsQuery
  ): Promise<CursorPaged<PublishedLegend>> {
    return CursorPaged.map(
      await this.api.legends.find(query),
      this.publishedLegends.set
    )
  }

  /**
   * Fetches a Published Legend by it's version ID.
   * @param legendVersionId
   */
  fetchLegendVersion(
    legendVersionId: LegendVersionId,
    supportedEngineVersion?: string
  ): Promise<PublishedLegend> {
    return this.api.legends
      .get(legendVersionId.id, legendVersionId.version, supportedEngineVersion)
      .then(this.publishedLegends.set)
  }

  /**
   * Fetches the latest version of a Published Legend.
   *
   * @param legendId
   */
  fetchLatestLegendVersion(legendId: Id) {
    return this.api.legends
      .get(Id.assert(legendId))
      .then(this.publishedLegends.set)
  }
}
